<template>
  <div class='add-new-service-provider-user-form'>
    <v-form ref="form">
      <div class='add-new-service-provider-user-details'>
        <v-text-field
          class="left-input"
          v-model="currentSPEmployee.firstName"
          :rules='[rules.required]'
          @change='formChanged'
          :label="$t('registration.client.info.firstname')"
          :placeholder="$t('registration.client.info.firstname.placeholder')"
          dense
          outlined
          required
          autofocus
        />
        <v-text-field
          :label="$t('registration.client.info.lastname')"
          :placeholder="$t('registration.client.info.lastname.placeholder')"
          :rules='[rules.required]'
          @change='formChanged'
          v-model='currentSPEmployee.lastName'
          dense
          outlined
          required
        />
        <v-text-field
          v-model='currentSPEmployee.workerProfile.jobTitle'
          :label="$t('registration.client.info.jobtitle')"
          :placeholder="$t('registration.client.info.jobtitle.placeholder')"
          :rules='[]'
          @change='formChanged'
          dense
          outlined
          required
        />
        <vue-tel-input-vuetify
          mode="international"
          v-model='currentSPEmployee.phone'
          :placeholder="this.$t('registration.client.info.phonenumber.placeholder')"
          :label="$t('registration.client.info.phonenumber')"
          :selectLabel="$t('registration.client.info.phonenumber.country')"
          @input='formChanged'
          :default-country='"il"'
          outlined
          dense
          required
        />
        <div class='error' v-if='!phoneNumberValid'> {{ $t('registration.client.info.phone.number.is.not.valid') }} </div>
        <buttons-switcher
          v-model="currentSPEmployee.workerProfile.workingDays"
          class='working-days'
          :items="spWorkerWorkingDaysOptions"
          :title="$t('add.sp.employee.form.working.days.title')"
        />
        <div class='service-provider-data-row'>
          <div class='left-side'>
            <v-text-field
              @change='formChanged'
              :rules='[rules.isNumber]'
              v-model='currentSPEmployee.workerProfile.experience'
              :label="$t('add.sp.employee.form.experience.title')"
              :placeholder="$t('add.sp.employee.form.rate.per.hour.placeholder')"
              dense
              outlined
              required
            />
          </div>
          <div class='right-side'>
            <v-text-field
              v-model='currentSPEmployee.workerProfile.hourRate'
              @change='formChanged'
              :rules='[rules.isNumber]'
              :label="$t('add.sp.employee.form.rate.per.hour.title')"
              :placeholder="$t('add.sp.employee.form.rate.per.hour.placeholder')"
              dense
              outlined
              required
            />
          </div>
        </div>
        <div class="additional-comment-section">
          <label class="label-section-title">
            {{ $t('add.sp.employee.form.short.bio.title') }}
          </label>
          <div class="details-data-section">
            <textarea
              v-model='currentSPEmployee.workerProfile.summary'
              maxlength="1000"
              @change='formChanged'
              :placeholder="$t('add.sp.employee.form.short.bio.placeholder')"
              class="form-control"
            ></textarea>
          </div>
        </div>
        <v-text-field
          :rules='[rules.hostWithRange]'
          @change='formChanged'
          v-model="currentSPEmployee.workerProfile.whitelistIP"
          :label="$t('registration.sp.freelancer.company.certification.white.ip.label')"
          :placeholder="$t('registration.sp.freelancer.company.certification.white.ip.placeholder')"
          dense
          outlined
          required
        />
        <div class="company-services-sub-title">
          {{ $t('sp.registration.company.services.select.services') }}
        </div>
        <v-list
          flat
          class="services-list"
        >
          <v-list-group
            v-for="service in topLevelServicesWithSubs"
            :key="service"
            :append-icon="''"
          >
            <template v-slot:activator>
              <v-list-item-icon>
                <v-icon color="#E7E9EC" small>add</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title v-text="$t(`${service}`)"></v-list-item-title>
              </v-list-item-content>
            </template>

            <v-list-item
              v-for="subService in formattedServicesOptions[service]"
              :key="subService"
            >
              <template>
                <v-list-item-action>
                  <custom-checkbox
                    color="cold-marine"
                    size="s"
                    :value="flatServicesOptionsObject[subService]"
                    @input="onCheck(subService)"
                  ></custom-checkbox>
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title v-text="$t(`${subService}`)"></v-list-item-title>
                </v-list-item-content>
              </template>
            </v-list-item>
          </v-list-group>
          <v-list-item
            v-for="service in topLevelServicesWithoutSubs"
            :key="service"
          >
            <template>
              <v-list-item-action>
                <custom-checkbox
                  :value="flatServicesOptionsObject[service]"
                  color="cold-marine"
                  @input="onCheck(service)"
                  size="s"
                ></custom-checkbox>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title v-text="$t(`${service}`)"></v-list-item-title>
              </v-list-item-content>
            </template>
          </v-list-item>
        </v-list>
        <div class='save-new-service-provider-data'>
          <v-btn class='save-btn' @click='saveEditedUser' :disabled='saveDisabled'> {{ $t('add.sp.employee.form.save.button')}} </v-btn>
        </div>
      </div>
    </v-form>
    <div class='add-new-service-provider-user-avatar'>
      <avatar-upload  class="avatar" :file-url="avatarUrl" @changed='avatarChanged'/>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AvatarUpload from '@/components/common/AvatarUpload.vue';
import {
  LOAD_CERTIFICATES,
  LOAD_SERVICES,
} from '@/store/spFreelancerRegistration/actionTypes';
import ButtonsSwitcher from '@/components/common/ButtonsSwitcher.vue';
import { rules } from '@/utils/validationUtils';
import { spWorkerWorkingDays } from '@/constants/registration';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';

export default {
  name: 'EditSPEmployeeProfileBody',
  components: {
    AvatarUpload,
    ButtonsSwitcher,
    CustomCheckbox,
  },
  created() {
    this.$store.dispatch(LOAD_SERVICES);
    this.$store.dispatch(LOAD_CERTIFICATES);
  },
  mounted() {
    if (this.rules.phone(this.currentSPEmployee.phone) === true) {
      this.phoneNumberValid = true;
    }
    this.valid = this.$refs.form.inputs.every(input => input.valid) && this.phoneNumberValid;
  },
  data() {
    return {
      phoneNumberValid: false,
      valid: false,
    };
  },
  watch: {
    'currentSPEmployee.phone': {
      handler() {
        this.phoneNumberValid = this.rules.phone(this.currentSPEmployee.phone) === true;
      },
      deep: true,
    },
  },
  methods: {
    formChanged() {
      this.valid = this.$refs.form.inputs.every(input => input.valid) && this.phoneNumberValid;
      this.$emit('formChanged');
    },
    async saveEditedUser() {
      this.valid = this.$refs.form.inputs.every(input => input.valid) && this.phoneNumberValid;
      if (this.valid) {
        await this.$emit('saveEditedUser');
      }
    },
    async avatarChanged(file) {
      this.currentSPEmployee.avatarURL = file;
    },
    onCheck(service) {
      let chosenFullService = {};
      this.spFreelancerServices.forEach(serviceOption => {
        if (serviceOption.shortName === service) {
          chosenFullService = serviceOption;
        }
      });
      let isInServices = false;
      let indexOfService = -1;

      for (let index = 0; index < this.currentSPEmployee.workerProfile.services.length; index += 1) {
        const serviceItem = this.currentSPEmployee.workerProfile.services[index];
        if (serviceItem.shortName === chosenFullService.shortName) {
          isInServices = true;
          indexOfService = index;
          break;
        }
      }
      if (!isInServices) {
        this.currentSPEmployee.workerProfile.services.push(chosenFullService);
      } else {
        this.currentSPEmployee.workerProfile.services.splice(indexOfService, 1);
      }
    },
    mapToObj(map) {
      const obj = {};
      map.forEach((value, key) => {
        obj[key] = value;
      });
      return obj;
    },
  },
  computed: {
    avatarUrl() {
      if (!this.currentSPEmployee?.avatarURL || this.currentSPEmployee?.avatarURL?.startsWith('data')) {
        return this.currentSPEmployee?.avatarURL;
      }
      return `${this.currentSPEmployee?.avatarURL}?${Date.now().toString()}` || null;
    },
    spWorkerWorkingDaysOptions() {
      return spWorkerWorkingDays;
    },
    ...mapGetters([
      'spFreelancerServices',
      'currentSPEmployee',
      'profile',
    ]),
    topLevelServicesWithoutSubs() {
      return Object.keys(this.formattedServicesOptions).filter(service => !this.formattedServicesOptions[service].length);
    },
    topLevelServicesWithSubs() {
      return Object.keys(this.formattedServicesOptions).filter(service => this.formattedServicesOptions[service].length);
    },
    formattedServicesOptions() {
      const servicesMap = new Map();
      this.spFreelancerServices.forEach(service => {
        if (service.shortName.includes('.sub.')) {
          const topLevelService = service.shortName.split('.sub.')[0];
          if (!servicesMap.has(topLevelService)) {
            servicesMap.set(topLevelService, [service.shortName]);
          } else {
            const currentSubServices = servicesMap.get(topLevelService);
            currentSubServices.push(service.shortName);
            servicesMap.set(topLevelService, currentSubServices);
          }
        } else if (!servicesMap.has(service.shortName)) {
          servicesMap.set(service.shortName, []);
        }
      });
      return this.mapToObj(servicesMap);
    },
    flatServicesOptionsObject() {
      const flatServicesObject = {};
      const serviceProviderServices = this.currentSPEmployee.workerProfile.services.map(service => service.shortName);
      this.topLevelServicesWithoutSubs.forEach(
        service => {
          flatServicesObject[service] = serviceProviderServices.includes(service);
        },
      );
      this.topLevelServicesWithSubs.forEach(
        service => {
          this.formattedServicesOptions[service].forEach(
            subService => {
              flatServicesObject[subService] = serviceProviderServices.includes(subService);
            },
          );
        },
      );
      return flatServicesObject;
    },
    saveDisabled() {
      return !this.valid;
    },
    rules() {
      return {
        ...rules,
        // eslint-disable-next-line no-restricted-globals
        isNumber: v => (!isNaN(v) && v >= 0) || this.$t('only.numbers'),
        phone: v => rules.phone(v) || this.$t('registration.client.info.phone.number.is.not.valid'),
        email: v => rules.email(v) || this.$t('registration.client.info.email.is.not.valid'),
        required: v => rules.required(v) || this.$t('required'),
        matchWithPassword: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          v === this.password || this.$t('registration.client.info.passwords.must.be.same'),
        requiredNumber: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          rules.requiredNumber(v) || this.$t('registration.client.info.passwords.at.least.one.number'),
        requiredUpperCase: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          rules.requiredUpperCase(v) || this.$t('registration.client.info.passwords.at.least.one.capital'),
        requiredLowerCase: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          rules.requiredLowerCase(v) || this.$t('registration.client.info.passwords.at.least.one.lower'),
        requiredSpecialSymbol: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          rules.requiredSpecialSymbol(v) || this.$t('registration.client.info.passwords.at.least.one.symbol'),
        endsWithCompanyDomain: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          (!!v && v.endsWith(this.profile.domain)) ||
          this.$t('registration.client.info.email.should.be.in.company.domain'),
      };
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.add-new-service-provider-user-form {
  background: var(--bg-primary);
  box-shadow: 0 0 rem(4) 0 rgba(14, 31, 51, 0.13);
  display: flex;
  padding-left: rem(104);
  padding-top: rem(60);
  padding-right: rem(104);

  .add-new-service-provider-user-details {
    flex: 32%;
    margin-right: 8%;

    .error {
      background: transparent !important;
      text-transform: capitalize;
      color: var(--status-error);
      font-size: rem(10);
      font-weight: 600;
      letter-spacing: rem(1.12);
      line-height: rem(16);
      margin-bottom: rem(16);
    }
  }

  .add-new-service-provider-user-avatar {
    flex: 1;
  }

  .services-list {
    background-color: var(--bg-secondary);
  }

  .technical-skills-section {
    margin-top: rem(60);

    .header-title-second {
      height: rem(24);
      width: rem(105);
      color: var(--text-primary);
      font-family: $font-family-medium;
      font-size: rem(20);
      font-weight: 500;
      letter-spacing: 0;
      line-height: rem(24);
    }
  }

  .service-provider-services {
    width: fit-content;
    color: var(--text-primary);
    font-family: $font-family-regular;

    .company-services-sub-title {
      height: rem(10) !important;
      width: rem(103) !important;
      color: var(--text-secondary) !important;
      font-size: rem(10) !important;
      letter-spacing: rem(0.8) !important;
      line-height: rem(10) !important;
      text-transform: uppercase !important;
      margin-bottom: rem(12) !important;
    }
  }

  .save-new-service-provider-data {
    padding-top: 10%;
    padding-bottom: 10%;

    .save-btn {
      height: rem(32);
      border-radius: $radius-sm;
      background-color: var(--brand-primary) !important;
      box-shadow: 0 rem(1) rem(1) 0 #dbdbdb, 0 rem(2) rem(1) rem(-1) #e0e0e0, 0 rem(1) rem(3) 0 #cccccc !important;
      color: var(--text-on-brand);
      font-size: rem(12);
      font-weight: 500;
      letter-spacing: rem(1.07);
      line-height: rem(16);
      text-align: center;
      margin-left: rem(8);
    }
  }

  .service-provider-certification {
    width: fit-content;
    margin-top: rem(24);
    color: var(--text-primary);
    font-family: $font-family-regular;

    .certification-options-section {
      height: rem(311);
      overflow-y: scroll;
      overflow-x: hidden;
      margin-bottom: rem(40);
    }

    .company-certification-sub-title {
      height: rem(10) !important;
      width: rem(103) !important;
      color: var(--text-secondary) !important;
      font-size: rem(10) !important;
      letter-spacing: rem(0.8) !important;
      line-height: rem(10) !important;
      text-transform: uppercase !important;
      margin-bottom: rem(12) !important;
    }

    .file-name {
      height: rem(16);
      color: var(--text-tertiary);
      font-family: $font-family-medium;
      font-size: rem(12);
      font-weight: 500;
      letter-spacing: rem(1.07);
      line-height: rem(16);
      text-align: center;
    }
  }

  .ip-section {
    width: fit-content;

    .company-ip-sub-title {
      height: rem(18);
      color: var(--text-primary);
      font-family: $font-family-regular;
      font-size: rem(14);
      letter-spacing: 0;
      line-height: rem(18);
    }

    .add-ip-form {
      margin-bottom: rem(32);

      .ips-inputs {
        display: flex;
        align-items: center;

        .text-field-section {
          flex: 0 0 rem(365);
        }

        .add-new-ip-section {
          flex: 0 0 rem(105);
          height: rem(16);
          color: #9BB0BE;
          font-family: $font-family-regular;
          font-size: rem(12);
          font-weight: 500;
          letter-spacing: rem(1.07);
          line-height: rem(16);
          text-align: center;

          .plus {
            font-size: rem(16);
          }

          .add-btn {
            width: rem(86);
            color: #FFFFFF;
          }
        }
      }
    }
  }

  .header-title {
    height: rem(24);
    width: fit-content;
    color: var(--text-primary);
    font-family: $font-family-medium;
    font-size: rem(20);
    font-weight: 500;
    letter-spacing: 0;
    line-height: rem(24);
  }

  .part {
    flex: 1;
  }

  .details-data-section {
    border-radius: $radius-sm;
    background-color: var(--bg-primary);
    box-shadow: 0 0 rem(8) 0 rgba(12, 18, 62, 0.08);
    margin-top: rem(24);

    .textarea {
      width: 100%;
      height: 100%;
      resize: none;
      padding: rem(8) rem(10) rem(10) rem(12);

      &:focus {
        outline: none !important;
        border: rem(1) solid var(--text-secondary);
        box-shadow: 0 0 rem(10) #719ece;
      }
    }
  }

  .service-provider-data-row {
    display: flex;
    padding-top: rem(24);

    .left-side {
      flex: 1;
      margin-right: 10%;
    }

    .right-side {
      flex: 1;
    }
  }

  .short-bio-text-field {
    .v-text-field {
      height: rem(141) !important;
    }
  }

  .label-section-title {
    height: rem(10) !important;
    width: rem(103) !important;
    color: var(--text-secondary) !important;
    font-size: rem(10) !important;
    letter-spacing: rem(0.8) !important;
    line-height: rem(10) !important;
    text-transform: uppercase !important;
    margin-bottom: rem(12) !important;
  }

  .additional-comment-section {
    margin-top: rem(24);

    textarea {
      width: 100%;
      height: 100%;
      resize: none;
      padding: rem(8) rem(10) rem(10) rem(12);

      &:focus {
        outline: none !important;
        border: rem(1) solid var(--text-secondary);
        box-shadow: 0 0 rem(10) #719ece;
      }
    }
  }

  .working-days {
    padding-top: rem(24);
  }

  .time-range {
    padding-top: rem(24);

    .working-hours {
      display: flex;
      flex-direction: column;

      .label-section-title {
        height: rem(10) !important;
        color: var(--text-secondary) !important;
        font-size: rem(10) !important;
        letter-spacing: rem(0.8) !important;
        line-height: rem(10) !important;
        text-transform: uppercase !important;
        margin-bottom: rem(12) !important;
      }

      .working-hours-picker {
        display: flex;
        flex-direction: row;

        .start-time {
          margin-right: rem(8);
        }

        .end-time {
          margin-right: rem(18);
          margin-top: auto;
        }

        .time-picker {
          flex: 1;
        }

        flex: auto;
      }
    }
  }
}
</style>
